import { createContext, ReactNode, useEffect } from "react";
import { useAppProperties } from "@/hooks";
import Color from "color";
import { BRAND_DEFAULTS, COLOR_SCHEME } from "@/theme.ts";

interface ContextValue {
  highlightHEX: string;
}

type OptionalContextValue = ContextValue | undefined;

const ensureColour = (hex?: string) => {
  if (!hex || hex.startsWith("#")) {
    return hex;
  }
  return `#${hex}`;
};

const ThemeContext = createContext<OptionalContextValue>(undefined);
const ThemeProvider = ({ children }: { children: ReactNode }) => {
  const { data, colorSchemeColors } = useAppProperties();
  const highlightColor =
    ensureColour(data?.HighlightHEX) ?? BRAND_DEFAULTS.brandPrimary;
  const watchedColor = ensureColour(data?.WatchedHEX);
  const scheduledColor = ensureColour(data?.ScheduledHEX);
  const missedColor = ensureColour(data?.MissedHEX);
  const journalColor = ensureColour(data?.JournalHEX);

  useEffect(() => {
    // Load AppProperty colours into CSS Variables for Tailwind to use
    if (highlightColor) {
      document.documentElement.style.setProperty(
        "--brandPrimary-color",
        highlightColor,
      );
      document.documentElement.style.setProperty(
        "--highlight-secondary-color",
        Color(highlightColor).darken(0.2).hex(),
      );
      document.documentElement.style.setProperty(
        "--highlight-tertiary-color",
        Color(highlightColor).darken(0.3).hex(),
      );
      document.documentElement.style.setProperty(
        "--highlight-bg-color",
        Color(highlightColor).alpha(0.15).rgb().string(),
      );
      document.documentElement.style.setProperty(
        "--highlight-bg-secondary-color",
        Color(highlightColor).alpha(0.2).rgb().string(),
      );
      document.documentElement.style.setProperty(
        "--highlight-bg-tertiary-color",
        Color(highlightColor).alpha(0.25).rgb().string(),
      );
    }
    if (watchedColor) {
      document.documentElement.style.setProperty(
        "--watched-color",
        watchedColor,
      );
    }
    if (scheduledColor) {
      document.documentElement.style.setProperty(
        "--scheduled-color",
        scheduledColor,
      );
    }
    if (missedColor) {
      document.documentElement.style.setProperty("--missed-color", missedColor);
    }
    if (journalColor) {
      document.documentElement.style.setProperty(
        "--journal-color",
        journalColor,
      );
    }

    Object.entries(colorSchemeColors ?? COLOR_SCHEME.LIGHT).forEach(
      ([key, value]) => {
        document.documentElement.style.setProperty(`--${key}-color`, value);
      },
    );
  }, [
    highlightColor,
    watchedColor,
    scheduledColor,
    missedColor,
    journalColor,
    colorSchemeColors,
  ]);

  return (
    <ThemeContext.Provider value={{ highlightHEX: highlightColor }}>
      {children}
    </ThemeContext.Provider>
  );
};
export { ThemeProvider, ThemeContext };
